import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { ErrorOutline, Refresh } from '@mui/icons-material';

/**
 * Error Boundary component for wizard steps
 * Catches errors in step components and displays a user-friendly error message
 */
class WizardErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details for debugging
    console.error('Wizard Error Boundary caught an error:', error, errorInfo);
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // You can also log the error to an error reporting service here
    // Example: logErrorToService(error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            padding: 4,
            textAlign: 'center',
            backgroundColor: '#f5f5f5',
            borderRadius: 2
          }}
        >
          <ErrorOutline 
            sx={{ 
              fontSize: 64, 
              color: '#d32f2f', 
              marginBottom: 2 
            }} 
          />
          <Typography variant="h5" gutterBottom sx={{ color: '#333', fontWeight: 'bold' }}>
            Something went wrong
          </Typography>
          <Typography variant="body1" sx={{ color: '#666', marginBottom: 3, maxWidth: '500px' }}>
            We encountered an error while loading this step. Please try refreshing the page or going back to the previous step.
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<Refresh />}
              onClick={this.handleReset}
              sx={{
                backgroundColor: '#0A71F5',
                '&:hover': {
                  backgroundColor: '#1a14b3'
                }
              }}
            >
              Try Again
            </Button>
            {this.props.onGoBack && (
              <Button
                variant="outlined"
                onClick={this.props.onGoBack}
                sx={{
                  borderColor: '#0A71F5',
                  color: '#0A71F5',
                  '&:hover': {
                    borderColor: '#1a14b3',
                    backgroundColor: 'rgba(43, 29, 232, 0.04)'
                  }
                }}
              >
                Go Back
              </Button>
            )}
          </Box>

          {/* Show error details in development mode */}
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <Box
              sx={{
                marginTop: 4,
                padding: 2,
                backgroundColor: '#fff',
                borderRadius: 1,
                border: '1px solid #ddd',
                maxWidth: '800px',
                textAlign: 'left',
                maxHeight: '300px',
                overflow: 'auto'
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', marginBottom: 1 }}>
                Error Details (Development Only):
              </Typography>
              <Typography variant="body2" component="pre" sx={{ 
                fontFamily: 'monospace', 
                fontSize: '12px',
                color: '#d32f2f',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}>
                {this.state.error.toString()}
                {this.state.errorInfo && (
                  <>
                    {'\n\n'}
                    {this.state.errorInfo.componentStack}
                  </>
                )}
              </Typography>
            </Box>
          )}
        </Box>
      );
    }

    // Render children normally if no error
    return this.props.children;
  }
}

export default WizardErrorBoundary;
